// Updated useSearchProductFetch.js
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as productActions from "../store/action/product";
import { useParams } from "react-router-dom";
import { throttle } from "lodash";

export const useSearchProductFetch = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { searchPagination } = useSelector((state) => state.product);
  const { city, name } = useParams();

  const searchProductFetch = useCallback(
    async (newPage = 1) => {
      if (isLoading) return;

      setIsLoading(true);
      try {
        await dispatch(productActions.getSearchProducts(city, name, newPage));
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, name, city, isLoading],
  );

  useEffect(() => {
    if (searchPagination) {
      setHasMore(page < searchPagination.totalPages);
    }
  }, [searchPagination, page]);

  // Throttled scroll handler
  const handleScroll = useCallback(
    throttle(() => {
      const scrollPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const pageHeight = document.documentElement.offsetHeight;
      const threshold = 500;

      if (pageHeight - scrollPosition < threshold && !isLoading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        searchProductFetch(nextPage);
      }
    }, 300),
    [isLoading, hasMore, page, searchProductFetch],
  );

  // Reset everything when city or name changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    searchProductFetch(1);
  }, [city, name]);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [handleScroll]);

  return { isLoading, hasMore };
};
